/* eslint-disable no-duplicate-imports */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { ApiLoadingState } from '../../middleware/api/apiList'
import ComplianceReminderApi from '../../middleware/api/ComplianceReminder/ComplianceReminderApi'
import { RootState } from '../../stateStore'
import { LoadingState, ValidationError } from '../../types/commonTypes'
import {
  AddCompliancePayload,
  GetAllCompliancesQueryParams,
  ComplianceTask,
  ComplianceReminderState,
  DepartmentWiseEmployeePayload,
  DepartmentWiseEmployeeResponse,
  DepartmentWiseEmployee,
  SubTask,
  ComplianceItem,
  ComplianceHistory,
  Department,
  UpcomingComplianceList,
  ComplianceDetails,
  ComplianceStatusType,
} from '../../types/ComplianceReminder/ComplianceReminderType'

const addNewCompliance = createAsyncThunk(
  '/compliance/addNewCompliance',
  async (AddNewCompliance: AddCompliancePayload, thunkApi) => {
    try {
      return await ComplianceReminderApi.addNewCompliance(AddNewCompliance)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const updateCompliance = createAsyncThunk(
  '/compliance/updateCompliance',
  async (updateComplianceDetails: ComplianceDetails, thunkApi) => {
    try {
      return await ComplianceReminderApi.updateCompliance(
        updateComplianceDetails,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getAllComplianceDetails = createAsyncThunk(
  'compliance/getAllComplianceDetails',
  async (props: GetAllCompliancesQueryParams, thunkApi) => {
    try {
      return await ComplianceReminderApi.getAllComplianceDetails(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getComplianceHistory = createAsyncThunk(
  'compliance/getComplianceHistory',
  async (id: number, thunkApi) => {
    try {
      const response = await ComplianceReminderApi.getComplianceHistory(id)
      return response.list
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getDepartmentList = createAsyncThunk(
  '/compliance/getDepartmentList',
  async (_, thunkApi) => {
    try {
      return await ComplianceReminderApi.getDepartmentList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getDepartmentWiseEmployeesList = createAsyncThunk<
  DepartmentWiseEmployeeResponse,
  DepartmentWiseEmployeePayload
>('/jobapplicant/getDepartmentWiseEmployeesList', async (props, thunkApi) => {
  try {
    return await ComplianceReminderApi.getDepartmentWiseEmployeesList(props)
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const curMonth = ''
/* ──────────────── Initial State ──────────────── */
const initialAddNewCompliance: ComplianceReminderState = {
  items: [] as ComplianceItem[],
  loading: ApiLoadingState.idle,
  error: null,
  departmentEmployees: [],
  ListSize: 0,
  getAllComplianceUnderTask: [] as SubTask[],
  getAllCompliance: [],
  uploadFileError: '',
  complianceHistory: [],
  tasks: [],
  isUploadingFile: false,
  departmentList: [],
  paginationCache: {
    currentPage: 0,
    pageSize: 20,
    searchJobTitle: '',
    status: '',
    department: '',
    dueDate: 'Current Year',
    complianceStatus: 'Pending',
    fromDate: '',
    toDate: '',
    complianceCycle: '',
    search: '',
  },
  total: 0,
  upcomingCompliance: [],
  updateCompliance: {
    id: 0,
    departmentName: '',
    complianceName: '',
    complianceCycle: '',
    dueDate: '',
    reasons: '',
    status: '',
    reminderIntervalDays: 0,
    employeeIds: [],
    complianceStatus: 'Pending' as ComplianceStatusType,
    employeeDto: [],
    completedDate: '',
    country: '',
  },
  selectedMonthValue: curMonth,
  getComplianceDetails: {
    id: 0,
    departmentName: '',
    complianceName: '',
    complianceCycle: '',
    dueDate: '',
    reasons: '',
    status: '',
    reminderIntervalDays: 0,
    employeeIds: [],
    complianceStatus: 'Pending' as ComplianceStatusType,
    employeeDto: [],
    completedDate: '',
    country: '',
  },
}

const complianceReminderSlice = createSlice({
  name: 'complianceReminder',
  initialState: initialAddNewCompliance,
  reducers: {
    setPaginationCache: (state, action) => {
      state.paginationCache = action.payload
    },
    clearPaginationCache: (state) => {
      state.paginationCache = {
        currentPage: 0,
        pageSize: 20,
        searchJobTitle: '',
        status: '',
        department: '',
        dueDate: 'Current Year',
        complianceStatus: 'Pending',
        fromDate: '',
        toDate: '',
        complianceCycle: '',
        search: '',
      }
    },
    setResponsiblePersonName: (
      state,
      action: PayloadAction<DepartmentWiseEmployee[]>,
    ) => {
      state.departmentEmployees = action.payload
    },
    setSelectedDepartment: (state, action: PayloadAction<Department[]>) => {
      state.departmentList = action.payload
    },
    setCurrentMonthValue: (state, action) => {
      state.selectedMonthValue = action.payload
    },
    setUpdateCompliance: (
      state,
      action: PayloadAction<{
        id: number
        complianceStatus: ComplianceStatusType
      }>,
    ) => {
      const { id, complianceStatus } = action.payload
      if (state.updateCompliance.id === id) {
        state.updateCompliance.complianceStatus = complianceStatus
      }
    },
    prependComplianceHistory: (
      state,
      action: PayloadAction<ComplianceHistory>,
    ) => {
      state.complianceHistory = [action.payload, ...state.complianceHistory]
    },
    updateComplianceHistoryItem: (
      state,
      action: PayloadAction<ComplianceHistory>,
    ) => {
      state.complianceHistory = state.complianceHistory.map((item) =>
        item.id === action.payload.id ? action.payload : item,
      )
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllComplianceDetails.pending, (state) => {
      state.loading = ApiLoadingState.loading
    })
    builder.addCase(getAllComplianceDetails.fulfilled, (state, action) => {
      state.loading = ApiLoadingState.succeeded
      state.getAllCompliance = action.payload.list
      state.ListSize = action.payload.size
    })
    builder.addCase(getAllComplianceDetails.rejected, (state) => {
      state.loading = ApiLoadingState.failed
    })

    builder.addCase(addNewCompliance.pending, (state) => {
      state.loading = ApiLoadingState.loading
      state.error = null
    })
    builder.addCase(addNewCompliance.fulfilled, (state) => {
      state.loading = ApiLoadingState.succeeded
    })
    builder.addCase(addNewCompliance.rejected, (state) => {
      state.loading = ApiLoadingState.failed
    })
    builder.addCase(updateCompliance.pending, (state) => {
      state.loading = ApiLoadingState.loading
      state.error = null
    })
    builder.addCase(updateCompliance.fulfilled, (state, action) => {
      state.loading = ApiLoadingState.succeeded
      const updated = action.payload as ComplianceDetails
      const index = state.getAllCompliance.findIndex((c) => c.id === updated.id)
      if (index !== -1) {
        state.getAllCompliance[index] = {
          ...state.getAllCompliance[index],
          ...updated,
          employeeDto: updated.employeeDto ?? [],
          employeeIds:
            updated.employeeIds ?? state.getAllCompliance[index].employeeIds,
        }
      }
      state.updateCompliance = updated
    })

    builder.addCase(updateCompliance.rejected, (state) => {
      state.loading = ApiLoadingState.failed
    })
    builder.addCase(getComplianceHistory.pending, (state) => {
      state.loading = ApiLoadingState.loading
    })
    builder.addCase(getComplianceHistory.fulfilled, (state, action) => {
      state.loading = ApiLoadingState.succeeded
      state.complianceHistory = action.payload as ComplianceHistory[]
    })
    builder.addCase(getComplianceHistory.rejected, (state, action) => {
      state.loading = ApiLoadingState.failed
      state.error = action.payload as string
    })

    builder.addCase(getDepartmentWiseEmployeesList.pending, (state) => {
      state.loading = ApiLoadingState.loading
      state.error = null
    })
    builder.addCase(
      getDepartmentWiseEmployeesList.fulfilled,
      (state, action) => {
        state.departmentEmployees = action.payload.departmentWiseEmployeesList
        state.loading = ApiLoadingState.succeeded
      },
    )
    builder.addCase(
      getDepartmentWiseEmployeesList.rejected,
      (state, action) => {
        state.loading = ApiLoadingState.failed
        state.error = action.payload as string
      },
    )
    builder
      .addCase(getDepartmentList.pending, (state) => {
        state.error = null
      })
      .addCase(getDepartmentList.fulfilled, (state, action) => {
        state.loading = ApiLoadingState.succeeded
        state.departmentList = action.payload as Department[]
      })
      .addCase(getDepartmentList.rejected, (state, action) => {
        state.loading = ApiLoadingState.succeeded
        state.error = action.payload as string
      })
  },
})

const getAllCompliances = (state: RootState): ComplianceItem[] =>
  state.complianceReminder.getAllCompliance
const getAllComplianceData = (state: RootState): ComplianceDetails =>
  state.complianceReminder.getComplianceDetails
const getAllDepartment = (state: RootState): Department[] =>
  state.complianceReminder.departmentList ?? []
const selectComplianceTotal = (state: RootState): number =>
  state.complianceReminder.total
const selectComplianceLoading = (state: RootState): LoadingState =>
  state?.complianceReminder.loading
const selectComplianceError = (state: RootState): string | null =>
  state.complianceReminder.error
const selectComplianceHistory = (
  state: RootState,
): ComplianceHistory[] | null => state.complianceReminder.complianceHistory
const selectDepartmentEmployees = (
  state: RootState,
): DepartmentWiseEmployee[] => state.complianceReminder.departmentEmployees
const selectAllComplianceUnderTasks = (state: RootState): SubTask[] =>
  state.complianceReminder.getAllComplianceUnderTask
const isLoading = (state: RootState): LoadingState =>
  state.complianceReminder.loading
const listSize = (state: RootState): number => state.complianceReminder.ListSize
const selectAllSubTaskCompliance = (state: RootState): ComplianceTask[] =>
  state.complianceReminder.tasks
const selectIsUploadingFile = (state: RootState): boolean =>
  state.complianceReminder.isUploadingFile
const selectUpcomingComplianceList = (
  state: RootState,
): UpcomingComplianceList[] => state.complianceReminder.upcomingCompliance

const selectComplianceById =
  (id: number): ((state: RootState) => ComplianceDetails | undefined) =>
  (state: RootState): ComplianceDetails | undefined => {
    const foundCompliance = state.complianceReminder.getAllCompliance.find(
      (compliance: { id: number }) => compliance.id === Number(id),
    )
    if (foundCompliance) return foundCompliance
    const cached = state.complianceReminder.updateCompliance
    return cached && cached.id === Number(id) ? cached : undefined
  }

const complianceSelectors = {
  selectComplianceError,
  selectComplianceLoading,
  selectComplianceTotal,
  getAllCompliances,
  selectComplianceHistory,
  selectDepartmentEmployees,
  selectAllComplianceUnderTasks,
  isLoading,
  listSize,
  selectAllSubTaskCompliance,
  selectIsUploadingFile,
  getAllDepartment,
  selectUpcomingComplianceList,
  selectPaginationCache: (
    state: RootState,
  ): {
    currentPage: number
    pageSize: number
    searchJobTitle?: string
    status?: string
    department?: string
    dueDate?: string
    complianceStatus?: string
    fromDate?: string
    toDate?: string
    complianceCycle?: string
    search?: string
    country?: string
  } => state.complianceReminder.paginationCache,
  getAllComplianceData,
  selectComplianceById,
}

export const complianceThunks = {
  addNewCompliance,
  getAllComplianceDetails,
  updateCompliance,
  getComplianceHistory,
  getDepartmentWiseEmployeesList,
  getDepartmentList,
}

export const complianceService = {
  ...complianceThunks,
  actions: complianceReminderSlice.actions,
  selectors: complianceSelectors,
}

export default complianceReminderSlice.reducer
